import React, { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { X, Plus, Link2 } from 'lucide-react';

/* ───────────────────── 상수 ───────────────────── */
const COLORS = {
  nat: { main: '#1a8f6f', light: '#e8f5f1', text: '#15694f' },
  imp: { main: '#3b82f6', light: '#eff6ff', text: '#1d4ed8' },
  pon: { main: '#d97706', light: '#fffbeb', text: '#92400e' },
  bridge: { main: '#7c3aed', light: '#f5f3ff', text: '#5b21b6' },
  combo: { main: '#4f46e5', light: '#eef2ff', text: '#3730a3' },   // 조합 칩(크라운+브랜드, 2026-09-29)
};

const PROSTHESIS_TYPE_MAP = {
  'Zr Crown':   { prosthesis: 'Crown', material: '지르코니아' },
  'Zr Inlay':   { prosthesis: 'Inlay', material: '지르코니아' },
  'PFZ':        { prosthesis: 'Crown', material: 'PFZ' },
  'Gold Inlay': { prosthesis: 'Inlay', material: '골드' },
  'Gold Crown': { prosthesis: 'Crown', material: '골드' },
  'PFM':        { prosthesis: 'Crown', material: 'PFM' },
};

// 표준 명칭 (2026-07-30 대표 확정): 한글 보철물+재료 한 단어, 클릭 선택 — 치과 단축어 난립 방지
const STANDARD_NAT_CHIPS = [
  '지르코니아 크라운', '이맥스 크라운', 'PFM 크라운', '골드 크라운', '메탈 크라운',
  'PMMA 크라운(임시)', '컴퍼지트 크라운', '티타늄 크라운',
  '지르코니아 인레이', '이맥스 인레이', '골드 인레이', '컴퍼지트 인레이',
  '이맥스 비니어', '지르코니아 비니어', '왁스업',
  '하이브리드 인레이', '메탈 인레이', '서베이드 크라운', '포스트',
];
const STANDARD_TYPE_MAP = {
  '지르코니아 크라운': { prosthesis: 'Crown', material: '지르코니아' },
  '이맥스 크라운': { prosthesis: 'Crown', material: '이맥스' },
  'PFM 크라운': { prosthesis: 'Crown', material: 'PFM' },
  '골드 크라운': { prosthesis: 'Crown', material: '골드' },
  '메탈 크라운': { prosthesis: 'Crown', material: '메탈' },
  'PMMA 크라운(임시)': { prosthesis: 'Crown', material: 'PMMA' },
  '컴퍼지트 크라운': { prosthesis: 'Crown', material: '컴퍼지트' },
  '티타늄 크라운': { prosthesis: 'Crown', material: '티타늄' },
  '지르코니아 인레이': { prosthesis: 'Inlay', material: '지르코니아' },
  '이맥스 인레이': { prosthesis: 'Inlay', material: '이맥스' },
  '골드 인레이': { prosthesis: 'Inlay', material: '골드' },
  '컴퍼지트 인레이': { prosthesis: 'Inlay', material: '컴퍼지트' },
  '이맥스 비니어': { prosthesis: 'Veneer', material: '이맥스' },
  '지르코니아 비니어': { prosthesis: 'Veneer', material: '지르코니아' },
  '왁스업': { prosthesis: 'Waxup', material: '왁스' },
  '하이브리드 인레이': { prosthesis: 'Inlay', material: '하이브리드' },
  '메탈 인레이': { prosthesis: 'Inlay', material: '메탈' },
  '서베이드 크라운': { prosthesis: 'Surveyed Crown', material: '' },
  '포스트': { prosthesis: 'Post', material: '' },
};
// 구 기본 칩 — 표시에서는 빠지지만 기존 주문 로드·커스텀 중복 제거용으로 유지
const DEFAULT_NAT_CHIPS = ['Zr Crown', 'Zr Inlay', 'PFZ', 'Gold Inlay', 'Gold Crown', 'PFM'];
Object.assign(PROSTHESIS_TYPE_MAP, STANDARD_TYPE_MAP);
const DEFAULT_IMPLANTS = ['Osstem TS Mini', 'Osstem TS Regular', 'Osstem TS Wide', 'Osstem KS'];

const upperTeeth = [18,17,16,15,14,13,12,11,21,22,23,24,25,26,27,28];
const lowerTeeth = [48,47,46,45,44,43,42,41,31,32,33,34,35,36,37,38];

let _uid = 0;
const uid = () => ++_uid;

/* ───────────────────── 컴포넌트 ───────────────────── */
const ProsthesisSelector = forwardRef(({
  prosthesisGroups = [],
  setProsthesisGroups,
  bridgeGroups = [],
  setBridgeGroups,
  ponticGroups = [],
  setPonticGroups,
  implantGroups = [],
  setImplantGroups,
  teethDetails = {},
  setTeethDetails,
  selectedTeeth = [],
  setSelectedTeeth,
  prosthesisOptions = [],
  prosthesisMaterialMap = {},
  customProsthesisOptions = [],
  customMaterialMap = {},
  implantOptions = [],
  onAddProsthesis,
  onDeleteProsthesis,
  onAddImplant,
  onDeleteImplant,
  // 최소 클릭 (2026-09-29 대표 승인): 조합 칩(크라운+브랜드) — 직접 만든 것(users.customImplantCombos)·자동(최근 의뢰서 빈도순)
  implantCombos = [],        // [{ crown, brand }] 치과가 [조합 추가]로 만든 것
  recentCombos = [],         // [{ prosthesis, material, brand, count }] 최근 의뢰서에서 같은 치아에 함께 붙은 조합 (빈도순)
  onAddImplantCombo,
  onDeleteImplantCombo,
  storageKey = '',           // 켜둔 칩 localStorage 키(치과 uid)
  initialDecided = false     // 불러온 치아는 싱글/브릿지를 이미 정한 것으로 본다(의뢰서 수정 — 2026-11-02)
}, ref) => {
  /* ── 내부 상태 ── */
  const [toothTags, setToothTags] = useState({});           // { 12: [tag, ...], 13: [...] }
  const [internalBridgeGroups, setInternalBridgeGroups] = useState([]); // [{ id, teeth:[12,13] }]

  const [checkedTeeth, setCheckedTeeth] = useState(new Set()); // 브릿지 체크용
  const [showAddProsthesis, setShowAddProsthesis] = useState(false);
  const [showAddImplant, setShowAddImplant] = useState(false);
  const [newProsthesisName, setNewProsthesisName] = useState('');
  const [newImplantName, setNewImplantName] = useState('');

  const isInitialized = useRef(false);
  const skipSync = useRef(false);

  /* ── 사용 빈도 (자주 쓰는 보철물이 앞으로 — 대표 확정 2026-07-30) ── */
  const usageRef = useRef(null);
  if (usageRef.current === null) {
    try { usageRef.current = JSON.parse(localStorage.getItem('dcProsthesisUsage') || '{}'); }
    catch (_) { usageRef.current = {}; }
  }
  const bumpUsage = (name) => {
    if (!name) return;
    usageRef.current[name] = (usageRef.current[name] || 0) + 1;
    try { localStorage.setItem('dcProsthesisUsage', JSON.stringify(usageRef.current)); } catch (_) { /* 저장 실패 무시 */ }
  };

  /* ── 커스텀 보철 칩 목록 (중복 제거) ── */
  const customNatChips = useMemo(() => {
    const defaultSet = new Set([...DEFAULT_NAT_CHIPS, ...STANDARD_NAT_CHIPS]);
    const seen = new Set();
    const chips = [];
    customProsthesisOptions.forEach(opt => {
      const v = typeof opt === 'string' ? opt : (opt.value || opt.label);
      if (v && !defaultSet.has(v) && !seen.has(v)) {
        seen.add(v);
        chips.push(v);
      }
    });
    return chips;
  }, [customProsthesisOptions]);

  /* ── 표시 칩: 표준+커스텀, 자주 쓰는 순 (세션 시작 시점 기준 고정 정렬) ── */
  const sortedNatChips = useMemo(() => {
    const u = usageRef.current || {};
    const all = [...STANDARD_NAT_CHIPS, ...customNatChips];
    return all
      .map((name, i) => ({ name, i, cnt: u[name] || 0 }))
      .sort((a, b) => (b.cnt - a.cnt) || (a.i - b.i))
      .map(x => x.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customNatChips]);

  /* ── 임플란트 칩 목록 ── */
  const allImplantChips = useMemo(() => {
    const seen = new Set();
    const chips = [];
    implantOptions.forEach(opt => {
      const v = typeof opt === 'object' ? (opt.value || opt.label || opt.name) : opt;
      if (v && !seen.has(v)) {
        seen.add(v);
        chips.push(v);
      }
    });
    return chips;
  }, [implantOptions]);

  /* ── 조합 칩 목록 (2026-09-29): 직접 만든 것이 앞, 자동(최근 씀)은 뒤 5개 ── */
  const comboKey = (crown, brand) => `${crown}|||${brand}`;
  const customCombos = useMemo(() => {
    const seen = new Set();
    const out = [];
    (implantCombos || []).forEach(c => {
      if (!c || !c.crown || !c.brand) return;
      const k = comboKey(c.crown, c.brand);
      if (seen.has(k)) return;
      seen.add(k);
      out.push({ key: k, crown: c.crown, brand: c.brand, custom: true });
    });
    return out;
  }, [implantCombos]);
  const autoCombos = useMemo(() => {
    const customKeys = new Set(customCombos.map(c => c.key));
    const seen = new Set();
    const out = [];
    (recentCombos || []).forEach(c => {
      if (!c || !c.brand) return;
      // 구 주문(분해형 'Crown'+'지르코니아')은 칩 라벨로 역매핑
      const crown = c.crown || findNatLabel(c.prosthesis, c.material);
      if (!crown || crown === '보철물') return;
      const k = comboKey(crown, c.brand);
      if (customKeys.has(k) || seen.has(k)) return;
      seen.add(k);
      out.push({ key: k, crown, brand: c.brand, custom: false });
    });
    return out.slice(0, 5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentCombos, customCombos]);
  const allCombos = useMemo(() => [...customCombos, ...autoCombos], [customCombos, autoCombos]);

  /* ───── 초기화: props → toothTags 역변환 ───── */
  useEffect(() => {
    if (isInitialized.current) return;

    const tags = {};
    const addTag = (tooth, tag) => {
      if (!tags[tooth]) tags[tooth] = [];
      tags[tooth].push({ ...tag, uid: uid() });
    };

    // prosthesisGroups → nat 태그
    (prosthesisGroups || []).forEach(g => {
      const label = findNatLabel(g.prosthesis, g.material);
      (g.teeth || []).forEach(t => {
        addTag(t, {
          key: 'nat', type: 'nat', label,
          sub: null, prosthesis: g.prosthesis, material: g.material
        });
      });
    });

    // implantGroups → imp 태그
    (implantGroups || []).forEach(g => {
      (g.teeth || []).forEach(t => {
        addTag(t, {
          key: 'imp', type: 'imp',
          label: `임플란트 · ${g.brand}`,
          sub: g.brand, prosthesis: '', material: ''
        });
      });
    });

    // ponticGroups → pon 태그
    (ponticGroups || []).forEach(g => {
      (g.teeth || []).forEach(t => {
        addTag(t, {
          key: 'pon', type: 'pon', label: '폰틱',
          sub: null, prosthesis: '', material: ''
        });
      });
    });

    // bridgeGroups 역변환
    const bridges = (bridgeGroups || []).map(bg => ({
      id: uid(),
      teeth: [...(bg.teeth || [])].sort((a, b) => a - b)
    }));

    if (Object.keys(tags).length > 0 || bridges.length > 0) {
      skipSync.current = true;
      setToothTags(tags);
      setInternalBridgeGroups(bridges);
      if (initialDecided) setDecidedTeeth(new Set(Object.keys(tags).map(Number)));
      isInitialized.current = true;
    } else {
      isInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prosthesisGroups, implantGroups, ponticGroups, bridgeGroups]);

  /* ───── deriveOutput: toothTags → props 변환 & sync ───── */
  const deriveOutput = useCallback((tags, bridges) => {
    // 1) prosthesisGroups: nat 태그를 (prosthesis, material) 기준 그룹핑
    const natMap = {};
    Object.entries(tags).forEach(([tooth, tagList]) => {
      tagList.filter(t => t.type === 'nat').forEach(tag => {
        const key = `${tag.prosthesis}|||${tag.material}`;
        if (!natMap[key]) natMap[key] = { teeth: [], prosthesis: tag.prosthesis, material: tag.material };
        natMap[key].teeth.push(Number(tooth));
      });
    });
    const newProsthesisGroups = Object.values(natMap).map(g => ({
      ...g, teeth: [...new Set(g.teeth)].sort((a, b) => a - b)
    }));

    // 2) bridgeGroups
    const newBridgeGroups = bridges.map(bg => ({ teeth: [...bg.teeth].sort((a, b) => a - b) }));

    // 3) ponticGroups: pon 태그가 있는 치아 수집
    const ponTeeth = [];
    Object.entries(tags).forEach(([tooth, tagList]) => {
      if (tagList.some(t => t.type === 'pon')) ponTeeth.push(Number(tooth));
    });
    const newPonticGroups = ponTeeth.length > 0 ? [{ teeth: ponTeeth.sort((a, b) => a - b) }] : [];

    // 4) implantGroups: imp 태그를 brand 기준 그룹핑
    const impMap = {};
    Object.entries(tags).forEach(([tooth, tagList]) => {
      tagList.filter(t => t.type === 'imp').forEach(tag => {
        const brand = tag.sub || '';
        if (!impMap[brand]) impMap[brand] = { teeth: [], brand };
        impMap[brand].teeth.push(Number(tooth));
      });
    });
    const newImplantGroups = Object.values(impMap).map(g => ({
      ...g, teeth: [...new Set(g.teeth)].sort((a, b) => a - b)
    }));

    // 5) teethDetails
    const newTeethDetails = {};
    Object.entries(tags).forEach(([tooth, tagList]) => {
      const nat = tagList.find(t => t.type === 'nat');
      const imp = tagList.find(t => t.type === 'imp');
      const pon = tagList.find(t => t.type === 'pon');
      const inBridge = bridges.some(bg => bg.teeth.includes(Number(tooth)));
      newTeethDetails[Number(tooth)] = {
        prosthesis: nat?.prosthesis || '',
        material: nat?.material || '',
        // 기존 Shade 보존 — 위저드 재편집 시 지정한 Shade가 ''로 초기화되던 문제 (2026-07-19)
        shade: teethDetails?.[Number(tooth)]?.shade || '',
        shadeType: teethDetails?.[Number(tooth)]?.shadeType || 'classic',
        implant: imp?.sub || '',
        isPontic: !!pon,
        isBridge: inBridge
      };
    });

    // 6) selectedTeeth
    const newSelectedTeeth = Object.keys(tags).filter(t => tags[t].length > 0).map(Number).sort((a, b) => a - b);

    return { newProsthesisGroups, newBridgeGroups, newPonticGroups, newImplantGroups, newTeethDetails, newSelectedTeeth };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teethDetails]);   // shade 보존을 위해 최신 teethDetails 참조

  useEffect(() => {
    if (skipSync.current) {
      skipSync.current = false;
      return;
    }
    if (!isInitialized.current) return;

    const { newProsthesisGroups, newBridgeGroups, newPonticGroups, newImplantGroups, newTeethDetails, newSelectedTeeth } = deriveOutput(toothTags, internalBridgeGroups);

    setProsthesisGroups(newProsthesisGroups);
    setBridgeGroups(newBridgeGroups);
    setPonticGroups(newPonticGroups);
    setImplantGroups(newImplantGroups);
    setTeethDetails(newTeethDetails);
    setSelectedTeeth(newSelectedTeeth);
  }, [toothTags, internalBridgeGroups]);

  /* ───── 헬퍼 ───── */
  function findNatLabel(prosthesis, material) {
    // 표준 칩('지르코니아 크라운' 등)을 먼저, 구 약식 칩('Zr Crown' 등)은 뒤에 — 둘 다 후보로 남겨 기존 로드 호환 유지
    for (const label of STANDARD_NAT_CHIPS) {
      const map = STANDARD_TYPE_MAP[label];
      if (map && map.prosthesis === prosthesis && map.material === material) return label;
    }
    for (const [label, map] of Object.entries(PROSTHESIS_TYPE_MAP)) {
      if (map.prosthesis === prosthesis && map.material === material) return label;
    }
    return prosthesis || material || '보철물';
  }

  function makeNatTag(chipLabel) {
    bumpUsage(chipLabel);
    // 단축어 원문을 prosthesis로 저장 (2026-07-19) — 기본 칩도 'Zr Crown' 그대로.
    // (기존: 'Crown'+'지르코니아'로 분해 저장 → 단가표 'Zr Crown' 정확일치·부분포함 모두 실패)
    // 구 주문의 분해형 데이터는 findNatLabel이 역매핑하므로 로드 호환 유지.
    const mapped = PROSTHESIS_TYPE_MAP[chipLabel];
    if (mapped) {
      return { key: 'nat', type: 'nat', label: chipLabel, sub: null, prosthesis: chipLabel, material: mapped.material };
    }
    // 커스텀
    const mats = customMaterialMap[chipLabel];
    return {
      key: 'nat', type: 'nat', label: chipLabel, sub: null,
      prosthesis: chipLabel,
      material: (mats && mats.length > 0) ? mats[0] : ''
    };
  }

  function makeImpTag(brand) {
    return { key: 'imp', type: 'imp', label: `임플란트 · ${brand}`, sub: brand, prosthesis: '', material: '' };
  }

  function makePonTag() {
    return { key: 'pon', type: 'pon', label: '폰틱', sub: null, prosthesis: '', material: '' };
  }

  /* ───── 폰틱·브릿지 (2026-11-02 대표 "단계별로 고르기는 폰틱·브릿지만 남기고 모달 없이 치식에서 바로") ─────
     · 옛 4단계 위저드(모달)는 없앴다 — 보철물·브랜드는 위 칩과 겹침. 단축어·브랜드 [+ 추가]는 칩 줄로 옮김
     · [브릿지] 켜기 → 치아 현황에서 묶을 치아를 누름 → [브릿지로 묶기]. 보철·임플란트·폰틱 중 하나라도 붙은 치아면 묶을 수 있다
     · 치아 2개 이상인데 싱글/브릿지를 안 정했으면 안내(보내기 전엔 CreateOrder 가 막음 — ref.bridgeDecisionNeeded) */
  const [bridgeMode, setBridgeMode] = useState(false);
  const [hintFlash, setHintFlash] = useState(false);                // 칩 없이 치아 클릭 등 — 안내 강조 (1.5초)
  const [chartHint, setChartHint] = useState('');                   // 치아 현황 머리줄 안내 한 줄
  const [decidedTeeth, setDecidedTeeth] = useState(() => new Set()); // 싱글/브릿지를 정한 치아(모두 싱글·브릿지 묶기 때 그 순간 치아들)
  const decisionRef = useRef(null);
  const flashTimer = useRef(null);

  /* ───── 켜둔 칩 (2026-09-29 최소 클릭) ─────
     칩(조합/보철물/브랜드/폰틱) 하나를 켜두면 메인 치식도 클릭만으로 바로 붙는다(모달 없음).
     마지막으로 켠 칩은 localStorage(치과 uid 키)에 기억 → 다음 의뢰서도 켜진 채 시작. */
  const ACTIVE_CHIP_KEY = `dcActiveChip_${storageKey || 'anon'}`;
  const [activeChip, setActiveChip] = useState(() => {
    try {
      const raw = localStorage.getItem(ACTIVE_CHIP_KEY);
      const v = raw ? JSON.parse(raw) : null;
      return (v && v.kind && v.key) ? { kind: v.kind, key: v.key } : null;
    } catch (e) {
      console.error('[보철선택] 켜둔 칩 복원 실패:', e?.message || e);
      return null;
    }
  });
  const setActiveChipPersist = (next) => {
    setActiveChip(next);
    try {
      if (next) localStorage.setItem(ACTIVE_CHIP_KEY, JSON.stringify(next));
      else localStorage.removeItem(ACTIVE_CHIP_KEY);
    } catch (e) {
      console.error('[보철선택] 켜둔 칩 저장 실패:', e?.message || e);
    }
  };
  const toggleActiveChip = (kind, key) => {
    if (bridgeMode) { setBridgeMode(false); setCheckedTeeth(new Set()); setActiveChipPersist({ kind, key }); return; }
    const same = activeChip && activeChip.kind === kind && activeChip.key === key;
    setActiveChipPersist(same ? null : { kind, key });
  };
  // 목록에 없는 칩(삭제됨·아직 로딩 전)은 켜진 것으로 취급하지 않는다
  const resolvedActive = useMemo(() => {
    if (!activeChip || bridgeMode) return null;   // 브릿지 모드 중엔 칩이 꺼진 것으로 본다
    if (activeChip.kind === 'combo') {
      const c = allCombos.find(x => x.key === activeChip.key);
      return c ? { ...activeChip, combo: c } : null;
    }
    if (activeChip.kind === 'nat') return sortedNatChips.includes(activeChip.key) ? activeChip : null;
    if (activeChip.kind === 'imp') return allImplantChips.includes(activeChip.key) ? activeChip : null;
    if (activeChip.kind === 'pon') return activeChip;
    return null;
  }, [activeChip, allCombos, sortedNatChips, allImplantChips, bridgeMode]);
  const activeChipName = !resolvedActive ? ''
    : resolvedActive.kind === 'combo' ? `${resolvedActive.combo.crown} · ${resolvedActive.combo.brand}`
    : resolvedActive.kind === 'pon' ? '폰틱'
    : resolvedActive.key;

  // [+ 조합 추가] 폼
  const [showAddCombo, setShowAddCombo] = useState(false);
  const [newComboCrown, setNewComboCrown] = useState('');
  const [newComboBrand, setNewComboBrand] = useState('');
  const handleAddComboSubmit = () => {
    const crown = newComboCrown || sortedNatChips[0] || '';
    const brand = newComboBrand || allImplantChips[0] || '';
    if (!crown || !brand) return;
    const k = comboKey(crown, brand);
    if (!customCombos.some(c => c.key === k) && onAddImplantCombo) onAddImplantCombo(crown, brand);
    setActiveChipPersist({ kind: 'combo', key: k });
    setShowAddCombo(false);
  };

  // 부모(CreateOrder)에서 "크라운 붙이러 가기"·싱글/브릿지 확인 → ref 함수는 아래(싱글/브릿지 계산 뒤)에서 연다
  const rootRef = useRef(null);

  const triggerHintFlash = (text) => {
    setChartHint(text || '');
    setHintFlash(true);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => { setHintFlash(false); setChartHint(''); }, 2500);
  };
  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);

  const toggleBridgeMode = () => {
    setCheckedTeeth(new Set());
    setBridgeMode(b => !b);
  };

  /* ───── 태그 토글 적용 (치아 클릭·대기 치아 공용) ───── */
  const applyTag = (toothNum, tag) => {
    setToothTags(prev => {
      const existing = prev[toothNum] || [];
      const same = existing.find(t => t.type === tag.type && t.label === tag.label);
      if (same) {
        // 같은 태그 재클릭 → 제거 (토글)
        const filtered = existing.filter(t => t.uid !== same.uid);
        if (filtered.length === 0) { const copy = { ...prev }; delete copy[toothNum]; return copy; }
        return { ...prev, [toothNum]: filtered };
      }
      // 같은 종류의 다른 항목은 교체 (치아당 종류별 1개), 다른 종류와는 공존
      const updated = [...existing.filter(t => t.type !== tag.type), { ...tag, uid: uid() }];
      return { ...prev, [toothNum]: updated };
    });
  };

  /* ───── 조합 칩 적용 (2026-09-29): nat+imp 두 태그를 함께 붙임. 같은 조합이 이미 있으면 둘 다 제거(토글) ─────
     저장 스키마는 그대로 — deriveOutput이 nat→prosthesisGroups, imp→implantGroups로 나눠 담는다. */
  const applyCombo = (toothNum, crown, brand) => {
    const natTag = makeNatTag(crown);
    const impTag = makeImpTag(brand);
    setToothTags(prev => {
      const existing = prev[toothNum] || [];
      const hasNat = existing.some(t => t.type === 'nat' && t.label === natTag.label);
      const hasImp = existing.some(t => t.type === 'imp' && t.label === impTag.label);
      if (hasNat && hasImp) {
        const filtered = existing.filter(t => t.type !== 'nat' && t.type !== 'imp');
        if (filtered.length === 0) { const copy = { ...prev }; delete copy[toothNum]; return copy; }
        return { ...prev, [toothNum]: filtered };
      }
      const updated = [
        ...existing.filter(t => t.type !== 'nat' && t.type !== 'imp'),
        { ...natTag, uid: uid() },
        { ...impTag, uid: uid() },
      ];
      return { ...prev, [toothNum]: updated };
    });
  };

  /* ───── 메인 치식도 클릭: 켜진 칩이 있으면 모달 없이 즉시 적용, 없으면 기존 위저드 ───── */
  const handleMainToothClick = (toothNum) => {
    if (bridgeMode) {
      if (!toothTags[toothNum]) { triggerHintFlash(`#${toothNum} — 먼저 보철이나 임플란트(또는 폰틱)를 붙여 주세요`); return; }
      toggleCheck(toothNum);
      return;
    }
    if (!resolvedActive) { triggerHintFlash('위에서 칩을 먼저 골라 주세요 (보철물·임플란트 브랜드·폰틱)'); return; }
    if (resolvedActive.kind === 'combo') applyCombo(toothNum, resolvedActive.combo.crown, resolvedActive.combo.brand);
    else if (resolvedActive.kind === 'nat') applyTag(toothNum, makeNatTag(resolvedActive.key));
    else if (resolvedActive.kind === 'imp') applyTag(toothNum, makeImpTag(resolvedActive.key));
    else if (resolvedActive.kind === 'pon') applyTag(toothNum, makePonTag());
  };

  /* ───── 태그 제거 ───── */
  const removeTag = (toothNum, tagUid) => {
    setToothTags(prev => {
      const existing = prev[toothNum] || [];
      const filtered = existing.filter(t => t.uid !== tagUid);
      if (filtered.length === 0) {
        const copy = { ...prev };
        delete copy[toothNum];
        return copy;
      }
      return { ...prev, [toothNum]: filtered };
    });
  };

  /* ── 치아 행 전체 제거 ── */
  const removeToothAll = (toothNum) => {
    setToothTags(prev => {
      const copy = { ...prev };
      delete copy[toothNum];
      return copy;
    });
    // 브릿지에서도 제거
    setInternalBridgeGroups(prev =>
      prev.map(bg => ({ ...bg, teeth: bg.teeth.filter(t => t !== toothNum) }))
          .filter(bg => bg.teeth.length >= 2)
    );
    setCheckedTeeth(prev => { const s = new Set(prev); s.delete(toothNum); return s; });
  };

  /* ───── 브릿지 ───── */
  // 치아 인접 순서 (상악 우→좌, 하악 좌→우)
  const TOOTH_ORDER = [18,17,16,15,14,13,12,11,21,22,23,24,25,26,27,28,48,47,46,45,44,43,42,41,31,32,33,34,35,36,37,38];
  const isAdjacent = (a, b) => {
    const ia = TOOTH_ORDER.indexOf(a);
    const ib = TOOTH_ORDER.indexOf(b);
    if (ia === -1 || ib === -1) return false;
    return Math.abs(ia - ib) === 1;
  };

  // 연속 치아끼리 그룹 분리
  const getConsecutiveGroups = (teeth) => {
    const sorted = [...teeth].sort((a, b) => TOOTH_ORDER.indexOf(a) - TOOTH_ORDER.indexOf(b));
    const groups = [];
    let current = [sorted[0]];
    for (let i = 1; i < sorted.length; i++) {
      if (isAdjacent(sorted[i - 1], sorted[i])) {
        current.push(sorted[i]);
      } else {
        groups.push(current);
        current = [sorted[i]];
      }
    }
    groups.push(current);
    return groups;
  };

  const handleBridgeGroup = () => {
    if (checkedTeeth.size < 2) return;
    const teeth = [...checkedTeeth];
    const groups = getConsecutiveGroups(teeth);
    // 연속 2개 이상인 그룹만 브릿지로 등록
    const validGroups = groups.filter(g => g.length >= 2);
    if (validGroups.length === 0) {
      alert('연속된 치아가 없어 브릿지를 만들 수 없습니다.');
      return;
    }
    setInternalBridgeGroups(prev => [...prev, ...validGroups.map(g => ({ id: uid(), teeth: g }))]);
    setCheckedTeeth(new Set());
    // 묶은 순간 붙어 있던 치아는 모두 「정함」(묶이지 않은 치아는 싱글로 본다)
    setDecidedTeeth(prev => { const s = new Set(prev); Object.keys(toothTags).forEach(t => s.add(Number(t))); return s; });
    setBridgeMode(false);
  };

  const removeBridgeGroup = (bridgeId) => {
    const bg = internalBridgeGroups.find(b => b.id === bridgeId);
    setInternalBridgeGroups(prev => prev.filter(b => b.id !== bridgeId));
    // 묶음을 풀면 그 치아들은 다시 싱글/브릿지를 정해야 한다
    if (bg) setDecidedTeeth(prev => { const s = new Set(prev); bg.teeth.forEach(t => s.delete(t)); return s; });
  };

  const toggleCheck = (toothNum) => {
    setCheckedTeeth(prev => {
      const s = new Set(prev);
      if (s.has(toothNum)) s.delete(toothNum);
      else s.add(toothNum);
      return s;
    });
  };

  /* ───── 보철물/임플란트 추가 ───── */
  const handleAddProsthesisSubmit = () => {
    if (!newProsthesisName.trim() || !onAddProsthesis) return;
    onAddProsthesis(newProsthesisName.trim(), '');
    setNewProsthesisName('');
    setShowAddProsthesis(false);
  };

  const handleAddImplantSubmit = () => {
    if (!newImplantName.trim() || !onAddImplant) return;
    onAddImplant(newImplantName.trim());
    setNewImplantName('');
    setShowAddImplant(false);
  };

  /* ───── 정렬된 치아 목록 ───── */
  const sortedTaggedTeeth = useMemo(() => {
    return Object.keys(toothTags).map(Number).sort((a, b) => a - b);
  }, [toothTags]);

  /* ── 브릿지에 속한 치아 세트 ── */
  const bridgeTeethSet = useMemo(() => {
    const s = new Set();
    internalBridgeGroups.forEach(bg => bg.teeth.forEach(t => s.add(t)));
    return s;
  }, [internalBridgeGroups]);

  /* ── 치아가 어느 브릿지에 속하는지 ── */
  const toothBridgeMap = useMemo(() => {
    const m = {};
    internalBridgeGroups.forEach(bg => bg.teeth.forEach(t => { m[t] = bg.id; }));
    return m;
  }, [internalBridgeGroups]);

  /* ───── 싱글/브릿지 정했는지 (2026-11-02) ───── */
  const undecidedTeeth = useMemo(() => sortedTaggedTeeth.filter(t => !bridgeTeethSet.has(t) && !decidedTeeth.has(t)),
    [sortedTaggedTeeth, bridgeTeethSet, decidedTeeth]);
  const needsBridgeDecision = sortedTaggedTeeth.length >= 2 && undecidedTeeth.length > 0;
  const markAllSingle = () => {
    setDecidedTeeth(prev => { const s = new Set(prev); sortedTaggedTeeth.forEach(t => s.add(t)); return s; });
    setBridgeMode(false); setCheckedTeeth(new Set());
  };
  const startBridgeFromDecision = () => {
    setCheckedTeeth(new Set());
    setBridgeMode(true);
    if (rootRef.current && rootRef.current.scrollIntoView) rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  // 보내기 전 확인(CreateOrder) — 안 정했으면 true 를 돌려주고, 안내 상자로 데려간다
  const needsRef = useRef(false);
  needsRef.current = needsBridgeDecision;
  useImperativeHandle(ref, () => ({
    activateNatChip: () => {
      setBridgeMode(false); setCheckedTeeth(new Set());
      const crown = (resolvedActive?.kind === 'combo' && resolvedActive.combo.crown) || sortedNatChips[0] || null;
      if (crown) setActiveChipPersist({ kind: 'nat', key: crown });
      if (rootRef.current && rootRef.current.scrollIntoView) rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    bridgeDecisionNeeded: () => needsRef.current,
    showBridgeDecision: () => {
      triggerHintFlash('');
      if (decisionRef.current && decisionRef.current.scrollIntoView) decisionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
  }), [resolvedActive, sortedNatChips]);   // eslint-disable-line react-hooks/exhaustive-deps

  /* ───── 렌더링 ───── */
  return (
    <div ref={rootRef} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ═══ 섹션 A: 칩 켜두고 치아 클릭 (2026-09-29 최소 클릭, 대표 승인) ═══
          최근 조합(크라운+브랜드) / 보철물만 / 브랜드만·폰틱 — 하나만 켜짐(라디오). 단계별 위저드는 아래 고급 경로로 유지 */}
      <div style={S.card}>
        <div style={S.cardHeader}>
          <span style={S.stepNumber}>1</span>
          칩을 하나 켜고 치아를 누르세요
          <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 12, marginLeft: 6 }}>켜둔 칩은 다음 의뢰서에도 그대로 유지됩니다</span>
        </div>
        <div style={{ padding: '10px 16px 12px', display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* 최근 조합 */}
          <div>
            <div style={S.rowLabel}>
              최근 조합
              <span style={S.rowLabelSub}>크라운 + 임플란트 브랜드가 한 번에 붙습니다</span>
            </div>
            <div style={S.chipRow}>
              {allCombos.map(c => {
                const on = resolvedActive?.kind === 'combo' && resolvedActive.key === c.key;
                return (
                  <span key={c.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    <button type="button" onClick={() => toggleActiveChip('combo', c.key)}
                      style={{ ...S.mainChip,
                        backgroundColor: on ? COLORS.combo.main : COLORS.combo.light,
                        color: on ? '#fff' : COLORS.combo.text,
                        borderColor: on ? COLORS.combo.main : '#d1d5db' }}>
                      <span style={{ ...S.chipDot, backgroundColor: on ? '#fff' : COLORS.combo.main }} />
                      {c.crown} · {c.brand}
                      {!c.custom && <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.85, marginLeft: 2 }}>최근 씀</span>}
                    </button>
                    {c.custom && onDeleteImplantCombo && (
                      <button type="button" onClick={() => onDeleteImplantCombo(c.crown, c.brand)} style={S.chipDeleteBtn} title="조합 삭제"><X size={12} /></button>
                    )}
                  </span>
                );
              })}
              {onAddImplantCombo && !showAddCombo && (
                <button type="button" onClick={() => setShowAddCombo(true)} style={S.addBtn}><Plus size={14} /> 조합 추가</button>
              )}
              {allCombos.length === 0 && !showAddCombo && (
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{onAddImplantCombo ? '아직 없습니다 — [조합 추가]로 만들거나, 의뢰서를 보내면 자동으로 쌓입니다' : '아직 없습니다 — 의뢰서를 보내면 자동으로 쌓입니다'}</span>
              )}
            </div>
            {showAddCombo && (
              <div style={{ ...S.addForm, marginTop: 6, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' }}>
                <select value={newComboCrown || sortedNatChips[0] || ''} onChange={e => setNewComboCrown(e.target.value)} style={S.addSelect}>
                  {sortedNatChips.map(ch => <option key={ch} value={ch}>{ch}</option>)}
                </select>
                <span style={{ color: '#6b7280', fontWeight: 700 }}>+</span>
                <select value={newComboBrand || allImplantChips[0] || ''} onChange={e => setNewComboBrand(e.target.value)} style={S.addSelect}>
                  {allImplantChips.map(ch => <option key={ch} value={ch}>{ch}</option>)}
                </select>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" onClick={handleAddComboSubmit} style={S.addSubmit}>추가</button>
                  <button type="button" onClick={() => setShowAddCombo(false)} style={S.addCancel}>취소</button>
                </div>
              </div>
            )}
          </div>

          {/* 보철물만 */}
          <div>
            <div style={S.rowLabel}>보철물만</div>
            <div style={S.chipRow}>
              {sortedNatChips.map(ch => {
                const on = resolvedActive?.kind === 'nat' && resolvedActive.key === ch;
                return (
                  <button key={ch} type="button" onClick={() => toggleActiveChip('nat', ch)}
                    style={{ ...S.mainChip,
                      backgroundColor: on ? COLORS.nat.main : COLORS.nat.light,
                      color: on ? '#fff' : COLORS.nat.text,
                      borderColor: on ? COLORS.nat.main : '#d1d5db' }}>
                    <span style={{ ...S.chipDot, backgroundColor: on ? '#fff' : COLORS.nat.main }} />
                    {ch}
                  </button>
                );
              }).map((btn, i) => {
                const ch = sortedNatChips[i];
                return (customNatChips.includes(ch) && onDeleteProsthesis)
                  ? <span key={ch} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>{btn}<button type="button" onClick={() => onDeleteProsthesis(ch)} style={S.chipDeleteBtn} title="단축어 삭제"><X size={12} /></button></span>
                  : btn;
              })}
              {onAddProsthesis && !showAddProsthesis && (
                <button type="button" onClick={() => setShowAddProsthesis(true)} style={S.addBtn}><Plus size={14} /> 추가</button>
              )}
            </div>
            {showAddProsthesis && (
              <div style={{ ...S.addForm, marginTop: 6 }}>
                <input type="text" value={newProsthesisName} onChange={e => setNewProsthesisName(e.target.value)} placeholder="단축어 (예: Zr Cr, 골드인레이)" style={S.addInput} />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" onClick={handleAddProsthesisSubmit} style={S.addSubmit}>추가</button>
                  <button type="button" onClick={() => setShowAddProsthesis(false)} style={S.addCancel}>취소</button>
                </div>
              </div>
            )}
          </div>

          {/* 임플란트 브랜드만 */}
          <div>
            <div style={S.rowLabel}>
              임플란트 브랜드만
              <span style={S.rowLabelSub}>브랜드만 찍으면 보내기 전에 알려드립니다</span>
            </div>
            <div style={S.chipRow}>
              {allImplantChips.map(ch => {
                const on = resolvedActive?.kind === 'imp' && resolvedActive.key === ch;
                const canDelete = !DEFAULT_IMPLANTS.includes(ch) && onDeleteImplant;
                return (
                  <span key={ch} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    <button type="button" onClick={() => toggleActiveChip('imp', ch)}
                      style={{ ...S.mainChip,
                        backgroundColor: on ? COLORS.imp.main : COLORS.imp.light,
                        color: on ? '#fff' : COLORS.imp.text,
                        borderColor: on ? COLORS.imp.main : '#d1d5db' }}>
                      <span style={{ ...S.chipDot, backgroundColor: on ? '#fff' : COLORS.imp.main }} />
                      {ch}
                    </button>
                    {canDelete && (
                      <button type="button" onClick={() => onDeleteImplant(ch)} style={S.chipDeleteBtn} title="브랜드 삭제"><X size={12} /></button>
                    )}
                  </span>
                );
              })}
              {onAddImplant && !showAddImplant && (
                <button type="button" onClick={() => setShowAddImplant(true)} style={S.addBtn}><Plus size={14} /> 추가</button>
              )}
            </div>
            {showAddImplant && (
              <div style={{ ...S.addForm, marginTop: 6 }}>
                <input type="text" value={newImplantName} onChange={e => setNewImplantName(e.target.value)} placeholder="브랜드명 입력" style={S.addInput} />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" onClick={handleAddImplantSubmit} style={S.addSubmit}>추가</button>
                  <button type="button" onClick={() => setShowAddImplant(false)} style={S.addCancel}>취소</button>
                </div>
              </div>
            )}
          </div>

          {/* 폰틱 · 브릿지 (2026-11-02 — 옛 「단계별로 고르기」 자리. 모달 없이 아래 치아 현황에서 바로) */}
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
            <div style={S.rowLabel}>
              폰틱 · 브릿지
              <span style={S.rowLabelSub}>켜고 아래 치아를 누르세요 · 폰틱은 보철·임플란트와 같이 붙습니다</span>
            </div>
            <div style={S.chipRow}>
              {(() => {
                const on = resolvedActive?.kind === 'pon';
                return (
                  <button type="button" onClick={() => toggleActiveChip('pon', 'pon')}
                    style={{ ...S.mainChip,
                      backgroundColor: on ? COLORS.pon.main : COLORS.pon.light,
                      color: on ? '#fff' : COLORS.pon.text,
                      borderColor: on ? COLORS.pon.main : '#d1d5db' }}>
                    <span style={{ ...S.chipDot, backgroundColor: on ? '#fff' : COLORS.pon.main }} />
                    폰틱
                  </button>
                );
              })()}
              <button type="button" onClick={toggleBridgeMode}
                style={{ ...S.mainChip,
                  backgroundColor: bridgeMode ? COLORS.bridge.main : COLORS.bridge.light,
                  color: bridgeMode ? '#fff' : COLORS.bridge.text,
                  borderColor: bridgeMode ? COLORS.bridge.main : '#d1d5db' }}>
                <Link2 size={13} />
                브릿지{internalBridgeGroups.length > 0 ? ` ${internalBridgeGroups.length}개` : ''}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ 섹션 B: 치아 선택 카드 ═══ */}
      <div style={S.card}>
        <div style={S.cardHeader}>
          <span style={S.stepNumber}>2</span>
          치아 현황
          {sortedTaggedTeeth.length > 0 && (
            <span style={S.badge}>{sortedTaggedTeeth.length}개</span>
          )}
          <span style={{ fontWeight: chartHint ? 700 : 400, color: chartHint ? (hintFlash ? '#b45309' : '#92400e') : '#94a3b8', fontSize: 12, marginLeft: 'auto' }}>
            {chartHint ? chartHint
              : bridgeMode ? <>{'브릿지: 묶을 치아를 누르세요 · '}<b style={{ color: COLORS.bridge.text }}>{`${checkedTeeth.size}개 선택`}</b></>
              : resolvedActive
              ? <>{'켜진 칩: '}<b style={{ color: '#1e293b' }}>{activeChipName}</b>{' · 같은 치아를 다시 누르면 지워집니다'}</>
              : '칩을 켜고 치아를 누르세요'}
          </span>
        </div>
        <div style={{ padding: '12px 16px' }}>
          {/* 상악 */}
          <div style={S.teethRow}>
            {upperTeeth.map((t, i) => (
              <ToothButton key={t} num={t} tags={toothTags[t]} bridged={bridgeTeethSet.has(t)}
                active={false} checkedMark={bridgeMode && checkedTeeth.has(t)}
                onClick={() => handleMainToothClick(t)} needGap={i === 8} />
            ))}
          </div>
          {/* 구분선 */}
          <div style={S.divider} />
          {/* 하악 */}
          <div style={S.teethRow}>
            {lowerTeeth.map((t, i) => (
              <ToothButton key={t} num={t} tags={toothTags[t]} bridged={bridgeTeethSet.has(t)}
                active={false} checkedMark={bridgeMode && checkedTeeth.has(t)}
                onClick={() => handleMainToothClick(t)} needGap={i === 8} />
            ))}
          </div>
          {/* 브릿지 모드 도구줄 (2026-11-02) */}
          {bridgeMode && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10, padding: '8px 10px', borderRadius: 8, background: COLORS.bridge.light, border: `1px solid ${COLORS.bridge.main}` }}>
              <Link2 size={14} style={{ color: COLORS.bridge.main }} />
              <span style={{ fontSize: 12.5, color: COLORS.bridge.text, fontWeight: 600 }}>
                {checkedTeeth.size < 2 ? '이어진 치아를 2개 이상 누르세요' : `${checkedTeeth.size}개 선택됨 — #${[...checkedTeeth].sort((a, b) => a - b).join(', #')}`}
              </span>
              <span style={{ flex: 1 }} />
              <button type="button" onClick={() => { setCheckedTeeth(new Set()); setBridgeMode(false); }} style={S.bridgeToolbarBtn}>그만하기</button>
              <button type="button" onClick={handleBridgeGroup} disabled={checkedTeeth.size < 2}
                style={{ ...S.bridgeToolbarBtn, backgroundColor: checkedTeeth.size < 2 ? '#c4b5fd' : COLORS.bridge.main, color: '#fff', borderColor: COLORS.bridge.main, cursor: checkedTeeth.size < 2 ? 'not-allowed' : 'pointer' }}>
                <Link2 size={14} /> 브릿지로 묶기
              </button>
            </div>
          )}
          {/* 범례 */}
          <div style={S.legend}>
            {[
              { label: '보철', c: COLORS.nat.main },
              { label: '임플란트', c: COLORS.imp.main },
              { label: '폰틱', c: COLORS.pon.main },
              { label: '브릿지', c: COLORS.bridge.main },
            ].map(l => (
              <span key={l.label} style={S.legendItem}>
                <span style={{ ...S.legendDot, backgroundColor: l.c }} />{l.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ 섹션 C: 보철 내역 카드 ═══ */}
      {sortedTaggedTeeth.length > 0 && (
        <div style={S.card}>
          <div style={S.cardHeader}>보철 내역</div>

          {/* 싱글/브릿지 정하기 (2026-11-02 대표 "두 개 이상 선택하면 싱글, 브릿지 제작 여부를 선택해주세요") — 안 정하면 보내기 전에 막는다 */}
          {needsBridgeDecision && (
            <div ref={decisionRef} style={{ padding: '10px 16px', backgroundColor: hintFlash ? '#fef3c7' : '#fffbeb', borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', transition: 'background-color 0.25s' }}>
              <Link2 size={14} style={{ color: '#f59e0b', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#92400e', fontWeight: 700 }}>
                싱글, 브릿지 제작 여부를 선택해주세요
              </span>
              <span style={{ fontSize: 12, color: '#b45309' }}>{`#${undecidedTeeth.join(', #')}`}</span>
              <span style={{ flex: 1 }} />
              <button type="button" onClick={markAllSingle} style={S.bridgeToolbarBtn}>모두 싱글</button>
              <button type="button" onClick={startBridgeFromDecision}
                style={{ ...S.bridgeToolbarBtn, backgroundColor: COLORS.bridge.main, color: '#fff', borderColor: COLORS.bridge.main }}>
                <Link2 size={14} /> 브릿지로 묶기
              </button>
            </div>
          )}

          {/* 브릿지 툴바 — 항상 표시 */}
          <div style={S.bridgeToolbar}>
            {checkedTeeth.size < 2 ? (
              <div style={S.bridgeHintRow}>
                <Link2 size={14} style={{ color: COLORS.bridge.main, flexShrink: 0 }} />
                <span style={{ color: '#6b7280', fontSize: 12 }}>
                  치아를 체크하여 브릿지로 묶을 수 있습니다
                  {checkedTeeth.size === 1 && <span style={{ color: COLORS.bridge.main, fontWeight: 600 }}> (1개 더 선택하세요)</span>}
                </span>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.bridge.text }}>
                  {checkedTeeth.size}개 선택됨
                </span>
                <div style={{ flex: 1 }} />
                <button type="button" onClick={() => setCheckedTeeth(new Set())} style={S.bridgeToolbarBtn}>취소</button>
                <button type="button" onClick={handleBridgeGroup} style={{ ...S.bridgeToolbarBtn, backgroundColor: COLORS.bridge.main, color: '#fff', borderColor: COLORS.bridge.main }}>
                  <Link2 size={14} /> 브릿지로 묶기
                </button>
              </div>
            )}
          </div>

          <div style={{ padding: '8px 16px 16px' }}>
            {/* 브릿지 그룹 블록들 */}
            {internalBridgeGroups.map(bg => (
              <div key={bg.id} style={S.bridgeBlock}>
                <div style={S.bridgeBlockHeader}>
                  <span style={{ fontWeight: 600, color: COLORS.bridge.text }}>
                    <Link2 size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    브릿지 {bg.teeth.length}본
                    <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 6 }}>
                      #{bg.teeth.join(', #')}
                    </span>
                  </span>
                  <button type="button" onClick={() => removeBridgeGroup(bg.id)} style={S.bridgeUngroup}>그룹 해제</button>
                </div>
                {bg.teeth.map(t => (
                  <ToothRow key={t} num={t} tags={toothTags[t] || []} bridged
                    onRemoveTag={(tagUid) => removeTag(t, tagUid)}
                    onRemoveAll={() => removeToothAll(t)}
                    checked={checkedTeeth.has(t)}
                    onCheck={() => toggleCheck(t)}
                    inBridgeBlock />
                ))}
              </div>
            ))}

            {/* 싱글 치아 행 */}
            {sortedTaggedTeeth.filter(t => !bridgeTeethSet.has(t)).map(t => (
              <ToothRow key={t} num={t} tags={toothTags[t] || []} bridged={false}
                onRemoveTag={(tagUid) => removeTag(t, tagUid)}
                onRemoveAll={() => removeToothAll(t)}
                checked={checkedTeeth.has(t)}
                onCheck={() => toggleCheck(t)} />
            ))}
          </div>
        </div>
      )}

      {/* ═══ 섹션 D: 요약 카드 ═══ */}
      {sortedTaggedTeeth.length > 0 && (
        <div style={S.card}>
          <div style={S.cardHeader}>요약</div>
          <div style={{ padding: '8px 16px 16px', fontSize: 13 }}>
            {internalBridgeGroups.map(bg => (
              <div key={bg.id} style={S.summaryRow}>
                <span style={S.summaryTeeth}>#{bg.teeth.join(', #')}</span>
                <span style={{ ...S.summaryBadge, backgroundColor: COLORS.bridge.light, color: COLORS.bridge.text }}>브릿지 {bg.teeth.length}본</span>
                <span style={{ color: '#6b7280', marginLeft: 8 }}>
                  {bg.teeth.map(t => (toothTags[t] || []).map(tag => tag.label).join(', ')).join(' | ')}
                </span>
              </div>
            ))}
            {sortedTaggedTeeth.filter(t => !bridgeTeethSet.has(t)).map(t => (
              <div key={t} style={S.summaryRow}>
                <span style={S.summaryTeeth}>#{t}</span>
                <span style={{ ...S.summaryBadge, backgroundColor: '#f3f4f6', color: '#374151' }}>싱글</span>
                <span style={{ color: '#6b7280', marginLeft: 8 }}>
                  {(toothTags[t] || []).map(tag => tag.label).join(', ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
});
ProsthesisSelector.displayName = 'ProsthesisSelector';

/* ═══ 치아 버튼 서브컴포넌트 ═══ */
const ToothButton = React.memo(({ num, tags, bridged, active, onClick, needGap, checkedMark, dimmed, pendingMark }) => {
  const dots = (tags || []).map(t => t.type);
  const hasDot = dots.length > 0;

  return (
    <div
      onClick={onClick}
      style={{
        width: 42, height: 50, borderRadius: 10,
        backgroundColor: pendingMark ? COLORS.nat.light : checkedMark ? COLORS.bridge.light : hasDot ? '#f8fafc' : '#f3f4f6',
        border: pendingMark ? `2px solid ${COLORS.nat.main}` : checkedMark ? `2px solid ${COLORS.bridge.main}` : bridged ? `2px solid ${COLORS.bridge.main}` : hasDot ? '2px solid #cbd5e1' : '1px solid #e5e7eb',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 3,
        fontSize: 13, fontWeight: hasDot ? 700 : 500,
        color: hasDot ? '#1e293b' : '#6b7280',
        cursor: active && !dimmed ? 'pointer' : 'default',
        opacity: dimmed ? 0.35 : active ? 1 : (hasDot ? 1 : 0.7),
        transition: 'all 0.15s',
        marginLeft: needGap ? 12 : 0,
        flexShrink: 0, position: 'relative',
        boxShadow: active && !dimmed ? '0 0 0 2px rgba(59,130,246,0.15)' : 'none',
      }}
    >
      <span>{num}</span>
      {hasDot && (
        <div style={{ display: 'flex', gap: 3 }}>
          {[...new Set(dots)].map((type, i) => (
            <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: COLORS[type]?.main || '#999' }} />
          ))}
        </div>
      )}
      {bridged && (
        <span style={{
          position: 'absolute', top: -5, right: -5,
          width: 16, height: 16, borderRadius: '50%',
          backgroundColor: COLORS.bridge.main, color: '#fff',
          fontSize: 9, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>B</span>
      )}
    </div>
  );
});

/* ═══ 치아 행 서브컴포넌트 ═══ */
const ToothRow = React.memo(({ num, tags, bridged, onRemoveTag, onRemoveAll, checked, onCheck, inBridgeBlock }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '6px 0',
    borderBottom: '1px solid #f1f5f9',
  }}>
    {!inBridgeBlock && (
      <input type="checkbox" checked={checked} onChange={onCheck} style={{ accentColor: COLORS.bridge.main }} />
    )}
    {bridged && inBridgeBlock && (
      <span style={{
        width: 20, height: 20, borderRadius: 4,
        backgroundColor: COLORS.bridge.main, color: '#fff',
        fontSize: 10, fontWeight: 700,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>B</span>
    )}
    <span style={{ fontWeight: 600, fontSize: 13, minWidth: 32, color: '#1e293b' }}>#{num}</span>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1 }}>
      {tags.map(tag => (
        <span key={tag.uid} style={{
          display: 'inline-flex', alignItems: 'center', gap: 3,
          padding: '2px 8px', borderRadius: 12,
          fontSize: 11, fontWeight: 500,
          backgroundColor: COLORS[tag.type]?.light || '#f3f4f6',
          color: COLORS[tag.type]?.text || '#374151',
          border: `1px solid ${COLORS[tag.type]?.main || '#d1d5db'}40`,
        }}>
          {tag.label}
          <button type="button" onClick={() => onRemoveTag(tag.uid)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'inherit', opacity: 0.6 }}>
            <X size={10} />
          </button>
        </span>
      ))}
    </div>
    <button type="button" onClick={onRemoveAll}
      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#94a3b8' }}>
      <X size={16} />
    </button>
  </div>
));

/* ═══ 스타일 ═══ */
const S = {
  /* ── 최소 클릭 칩 줄 (2026-09-29) ── */
  rowLabel: {
    fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6,
    display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
  },
  rowLabelSub: { fontSize: 11, fontWeight: 400, color: '#94a3b8' },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' },
  mainChip: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '6px 12px', borderRadius: 16,
    fontSize: 12.5, fontWeight: 600,
    border: '1.5px solid', cursor: 'pointer',
    transition: 'all 0.12s', outline: 'none',
  },
  chipDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  addSelect: {
    padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db',
    fontSize: 13, backgroundColor: '#fff', color: '#1e293b', maxWidth: 220,
  },
  /* ── 순차 위저드 (2026-07-19) ── */
  stepLaunchBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 7,
    padding: '10px 16px', borderRadius: 10,
    border: '1.5px solid', fontSize: 14, fontWeight: 700,
    cursor: 'pointer', transition: 'all 0.15s',
  },
  stepLaunchNum: {
    width: 20, height: 20, borderRadius: '50%', color: '#fff',
    fontSize: 12, fontWeight: 800,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  },
  wzOverlay: {
    position: 'fixed', inset: 0, zIndex: 1000,
    backgroundColor: 'rgba(15,23,42,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16,
  },
  wzModal: {
    backgroundColor: '#fff', borderRadius: 16,
    // 치식도 32개 치아가 잘리지 않게 좌우 여유 확보 (2026-07-25)
    width: '100%', maxWidth: 860, maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
  },
  wzTabs: {
    display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
    padding: '12px 16px', borderBottom: '1px solid #f1f5f9',
    position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1,
  },
  wzTab: {
    padding: '7px 12px', borderRadius: 8, border: '1.5px solid',
    fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
  },
  wzChipArea: { padding: '12px 16px 4px' },
  wzHint: {
    marginTop: 8, padding: '8px 12px', borderRadius: 8,
    fontSize: 12.5, fontWeight: 600,
  },
  wzFooter: {
    display: 'flex', gap: 8, alignItems: 'center',
    padding: '12px 16px', borderTop: '1px solid #f1f5f9',
    position: 'sticky', bottom: 0, backgroundColor: '#fff',
  },
  wzDoneBtn: {
    padding: '10px 20px', borderRadius: 10, border: '1px solid #d1d5db',
    backgroundColor: '#fff', color: '#374151', fontSize: 14, fontWeight: 700, cursor: 'pointer',
  },
  wzNextBtn: {
    padding: '10px 20px', borderRadius: 10, border: 'none',
    color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  cardHeader: {
    padding: '12px 16px',
    fontWeight: 700,
    fontSize: 15,
    borderBottom: '1px solid #f1f5f9',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#1e293b',
  },
  stepNumber: {
    width: 24, height: 24, borderRadius: '50%',
    backgroundColor: '#1e293b', color: '#fff',
    fontSize: 13, fontWeight: 700,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  badge: {
    fontSize: 11, fontWeight: 600,
    backgroundColor: '#3b82f6', color: '#fff',
    padding: '2px 8px', borderRadius: 10,
  },
  topChip: {
    padding: '8px 18px', borderRadius: 20,
    fontSize: 13, fontWeight: 600,
    border: '1.5px solid', cursor: 'pointer',
    transition: 'all 0.15s',
    outline: 'none',
  },
  subArea: {
    padding: '8px 16px 12px',
    backgroundColor: '#fafbfc',
    borderTop: '1px solid #f1f5f9',
  },
  subLabel: {
    fontSize: 11, fontWeight: 600, color: '#6b7280',
    marginBottom: 6, textTransform: 'uppercase',
  },
  subChip: {
    padding: '5px 12px', borderRadius: 14,
    fontSize: 12, fontWeight: 500,
    border: '1px solid', cursor: 'pointer',
    transition: 'all 0.12s',
    outline: 'none',
  },
  chipDeleteBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    padding: 2, color: '#ef4444', display: 'flex',
    alignItems: 'center', borderRadius: 4,
  },
  addBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '4px 10px', borderRadius: 8,
    fontSize: 12, fontWeight: 500,
    backgroundColor: '#f3f4f6', border: '1px solid #d1d5db',
    cursor: 'pointer', color: '#374151',
  },
  addForm: {
    display: 'flex', flexDirection: 'column', gap: 6,
    padding: 8, backgroundColor: '#fff',
    borderRadius: 8, border: '1px solid #e2e8f0',
  },
  addInput: {
    padding: '6px 10px', borderRadius: 6,
    border: '1px solid #d1d5db', fontSize: 13,
    outline: 'none',
  },
  addSubmit: {
    padding: '6px 14px', borderRadius: 6,
    backgroundColor: '#3b82f6', color: '#fff',
    border: 'none', fontSize: 12, fontWeight: 600,
    cursor: 'pointer',
  },
  addCancel: {
    padding: '6px 14px', borderRadius: 6,
    backgroundColor: '#f3f4f6', color: '#374151',
    border: '1px solid #d1d5db', fontSize: 12, fontWeight: 500,
    cursor: 'pointer',
  },
  hintBar: {
    padding: '8px 16px',
    fontSize: 13, fontWeight: 500,
  },
  teethRow: {
    display: 'flex', gap: 5,
    justifyContent: 'center',
    overflowX: 'auto',
    padding: '2px 0',
  },
  divider: {
    height: 3, margin: '8px 0',
    background: 'linear-gradient(90deg, #ef4444, #f97316, #ef4444)',
    borderRadius: 2, opacity: 0.5,
  },
  legend: {
    display: 'flex', gap: 12, marginTop: 10,
    justifyContent: 'center', flexWrap: 'wrap',
  },
  legendItem: {
    display: 'flex', alignItems: 'center', gap: 4,
    fontSize: 11, color: '#6b7280',
  },
  legendDot: {
    width: 8, height: 8, borderRadius: '50%',
  },
  bridgeToolbar: {
    display: 'flex', gap: 8, padding: '10px 16px',
    borderBottom: '1px solid #f1f5f9',
    backgroundColor: COLORS.bridge.light,
    alignItems: 'center',
  },
  bridgeHintRow: {
    display: 'flex', alignItems: 'center', gap: 6,
  },
  bridgeToolbarBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '6px 14px', borderRadius: 8,
    fontSize: 12, fontWeight: 600,
    border: '1px solid #d1d5db',
    backgroundColor: '#fff', cursor: 'pointer',
    color: '#374151',
  },
  bridgeBlock: {
    border: `2px solid ${COLORS.bridge.main}`,
    borderRadius: 10, padding: 10,
    marginBottom: 8, backgroundColor: COLORS.bridge.light,
  },
  bridgeBlockHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    paddingBottom: 6, borderBottom: `1px solid ${COLORS.bridge.main}30`,
    marginBottom: 4, fontSize: 13,
  },
  bridgeUngroup: {
    background: 'none', border: '1px solid #d1d5db',
    borderRadius: 6, padding: '3px 8px',
    fontSize: 11, cursor: 'pointer', color: '#6b7280',
  },
  summaryRow: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '4px 0', borderBottom: '1px solid #f8fafc',
    flexWrap: 'wrap',
  },
  summaryTeeth: {
    fontWeight: 600, fontSize: 13, color: '#1e293b',
  },
  summaryBadge: {
    fontSize: 11, fontWeight: 600,
    padding: '1px 8px', borderRadius: 8,
  },
};

export default ProsthesisSelector;
